/* global SideDrawer */

if (!customElements.get("quick-add-drawer")) {
  class QuickAddDrawer extends SideDrawer {
    constructor() {
      super();
      this.content = this.querySelector(".js-product-details");
      this.footer = this.querySelector(".drawer__footer");
      this.form = this.querySelector("product-form");
      this.notification = this.querySelector(".js-added-to-cart");
      this.backBtn = this.querySelector(".drawer__back-btn");
      this.openCartDrawerLinks = this.querySelectorAll(".js-open-cart-drawer");
      this.cartDrawer = document.querySelector("side-cart");
      this.fetch = null;
      this.fetchedUrls = [];
      this.closeBtn = this.querySelector(".drawer__close-btn");
      this.quickAddButtonMouseEnterHandler =
        this.handleQuickAddButtonMouseEnter.bind(this);
      this.documentClickHandler = this.handleDocumentClick.bind(this);

      document.addEventListener("click", this.documentClickHandler);
      this.addEventListener(
        "on:variant:change",
        this.handleVariantChange.bind(this)
      );

      this.closeBtn.addEventListener("click", this.close.bind(this));

      this.openCartDrawerLinks.forEach((link) => {
        link.addEventListener("click", this.handleOpenCartClick.bind(this));
      });

      if (theme.device.hasHover && theme.mediaMatches.md) {
        document.querySelectorAll(".js-quick-add").forEach((button) => {
          this.bindQuickAddButtonMouseEnter(button);
        });

        if ("MutationObserver" in window) {
          this.observer = new MutationObserver((mutations) => {
            mutations.forEach((mutation) => {
              // Add event listener to new buttons
              mutation.addedNodes.forEach((node) => {
                if (node.nodeType === Node.ELEMENT_NODE) {
                  node.querySelectorAll(".js-quick-add").forEach((button) => {
                    this.bindQuickAddButtonMouseEnter(button);
                  });
                }
              });

              // Remove event listener from removed buttons
              mutation.removedNodes.forEach((node) => {
                if (node.nodeType === Node.ELEMENT_NODE) {
                  node.querySelectorAll(".js-quick-add").forEach((button) => {
                    button.removeEventListener(
                      "mouseenter",
                      this.quickAddButtonMouseEnterHandler
                    );
                  });
                }
              });
            });
          });

          // Start observing changes to the DOM
          this.observer.observe(document.body, {
            childList: true,
            subtree: true,
          });
        }
      }
    }

    disconnectedCallback() {
      document.removeEventListener("click", this.documentClickHandler);
      document.querySelectorAll(".js-quick-add").forEach((button) => {
        button.removeEventListener(
          "mouseenter",
          this.quickAddButtonMouseEnterHandler
        );
      });
      if (this.observer) this.observer.disconnect();
    }

    /**
     * Bind a mouseenter event to a given quick add button if it's not already bound
     * @param {Element} button - The button element to bind the mouseenter event to.
     */
    bindQuickAddButtonMouseEnter(button) {
      if (!button.dataset.quickAddListenerAdded) {
        button.dataset.quickAddListenerAdded = "true";
        button.addEventListener(
          "mouseenter",
          this.quickAddButtonMouseEnterHandler
        );
      }
    }

    /**
     * Start the fetch on hover of a quick add button for perceived performance gains
     * @param {object} evt - Event object.
     */
    handleQuickAddButtonMouseEnter(evt) {
      if (!this.fetchedUrls.includes(evt.target.dataset.productUrl)) {
        this.fetch = {
          url: evt.target.dataset.productUrl,
          promise: fetch(evt.target.dataset.productUrl),
        };
        this.fetchedUrls.push(evt.target.dataset.productUrl);
      }
    }

    /**
     * Handles 'click' events on success notification's 'View cart' link
     * @param {object} evt - Event object.
     */
    handleOpenCartClick(evt) {
      // Open the cart drawer if available on the page
      if (this.cartDrawer) {
        evt.preventDefault();
        this.cartDrawer.open();
      } else if (window.location.pathname === theme.routes.cart) {
        evt.preventDefault();
        this.close();
      }
    }

    /**
     * Handles 'click' events on the document.
     * @param {object} evt - Event object.
     */
    handleDocumentClick(evt) {
      if (!evt.target.matches(".js-quick-add")) return;

      // Close the cart drawer if it's open
      if (this.cartDrawer && this.cartDrawer.ariaHidden === "false") {
        const overlay = document.querySelector(".js-overlay.is-visible");
        if (overlay) overlay.style.transitionDelay = "200ms";

        this.cartDrawer.close();

        // Wait a few ms for a more optimal ux/animation
        setTimeout(() => {
          this.backBtn.hidden = false;
          this.open(evt.target);
          if (overlay) overlay.style.transitionDelay = "";
        }, 200);
      } else {
        this.open(evt.target);
      }
    }

    /**
     * Handles 'on:variant:change' events on the Quick Add drawer.
     * @param {object} evt - Event object.
     */
    handleVariantChange(evt) {
      let url = this.productUrl;

      if (evt.detail.variant) {
        const separator = this.productUrl.split("?").length > 1 ? "&" : "?";
        url += `${separator}variant=${evt.detail.variant.id}`;
      }

      this.querySelectorAll(".js-prod-link").forEach((link) => {
        link.href = url;
      });
    }

    /**
     * Opens the drawer and fetches the product details.
     * @param {Element} opener - Element that triggered opening of the drawer.
     */
    async open(opener) {
      opener.setAttribute("aria-disabled", "true");

      if (this.notification) this.notification.hidden = true;

      // If it's the same product as previously shown, there's no need to re-fetch the details.
      if (this.productUrl && this.productUrl === opener.dataset.productUrl) {
        super.open(opener);
        if (opener.dataset.selectedColor) this.setActiveVariant(opener);
        opener.removeAttribute("aria-disabled");
        return;
      }

      this.productUrl = opener.dataset.productUrl;
      this.template = "default";
      if (opener.dataset.template) {
        this.template = opener.dataset.template;
        this.secondFetch = opener.dataset.secondFetch;
      }

      this.content.innerHTML = "";
      this.classList.add("is-loading");
      this.content.classList.add("drawer__content--out");
      this.footer.classList.add("drawer__footer--out");

      super.open(opener);

      if (!this.fetch || this.fetch.url !== opener.dataset.productUrl) {
        this.fetch = {
          url: opener.dataset.productUrl,
          promise: fetch(opener.dataset.productUrl),
        };
      }

      if (this.template != "default") {
        this.fetch2 = {
          url: opener.dataset.productUrl + opener.dataset.secondFetch,
          promise: fetch(
            opener.dataset.productUrl + opener.dataset.secondFetch
          ),
        };
      }

      const response = await this.fetch.promise;
      const response2 = this.fetch2 ? await this.fetch2.promise : null;

      if (response.ok) {
        if (this.template != "default" && response2.ok) {
          const tmpl2 = document.createElement("template");
          tmpl2.innerHTML = await response2.text();
          this.productEl2 = tmpl2.content.querySelector(".js-product");
        }

        const tmpl = document.createElement("template");
        tmpl.innerHTML = await response.text();
        this.productEl = tmpl.content.querySelector(".js-product");
        this.renderProduct(opener);
      }

      this.fetch = null;

      opener.removeAttribute("aria-disabled");
    }

    /**
     * Closes the cart drawer.
     */
    close() {
      super.close(() => {
        this.backBtn.hidden = true;
      });
    }

    /**
     * Renders the product details.
     * @param {Element} opener - Element that triggered opening of the drawer.
     */
    renderProduct(opener) {
      // Replace instances of section id to prevent duplicates on the product page.
      if (this.template == "default") {
        const sectionId = this.productEl.dataset.section;
        this.productEl.innerHTML = this.productEl.innerHTML.replaceAll(
          sectionId,
          "quickadd"
        );

        // Prevent variant picker from updating the URL on change.
        const variantPicker = this.productEl.querySelector("variant-picker");
        if (variantPicker) variantPicker.dataset.updateUrl = "false";

        // Remove size chart modal and link (if they exist).
        const sizeChartModal = this.productEl.querySelector(
          '[data-modal="size-chart"]'
        );
        if (sizeChartModal) {
          sizeChartModal.remove();
        }
      }

      this.updateContent();
      this.updateForm();

      if (document.querySelector("[open-pack-builder]")) {
        this.querySelector(".quick-add__product-details").classList.add(
          "pack-builder"
        );
        document.querySelectorAll("[open-pack-builder]").forEach((button) => {
          button.addEventListener("click", () => {
            document.querySelector("pack-builder-modal").openDrawer();
          });
        });
      }

      // Update the product media.
      const activeMedia = this.productEl.querySelector(
        ".media-viewer__item.is-current-variant"
      );

      if (activeMedia) this.updateMedia(activeMedia.dataset.mediaId);

      if (this.productEl.querySelector("product-media .swiper-main")) {
        this.updateMedia(
          this.productEl.querySelector(
            "product-media .swiper-main .is-current-variant .swiper-slide__image"
          ).dataset.mediaId
        );
      }

      if (opener.dataset.selectedColor) {
        // Timeout to allow the VariantPicker to initialize
        setTimeout(this.setActiveVariant.bind(this, opener), 10);
      }
      if (this.querySelector(".js-product-form-main .selling__inputs")) {
        setTimeout(
          this.setActiveVariant.bind(
            this.querySelector(
              ".js-product-form-main .selling__inputs"
            ).remove()
          ),
          10
        );
      }
    }

    /**
     * Set color variant to match the one selected in the card.
     * @param {Element} opener - Element that triggered opening of the drawer.
     */
    setActiveVariant(opener) {
      const colorOptionBox = this.querySelector(
        `.opt-btn[value="${opener.dataset.selectedColor}"]`
      );
      if (colorOptionBox) {
        this.querySelector(
          `.opt-btn[value="${opener.dataset.selectedColor}"]`
        ).click();
      } else {
        const colorOptionDropdown = this.querySelector(
          `.custom-select__option[data-value="${opener.dataset.selectedColor}"]`
        );
        if (colorOptionDropdown) {
          const customSelect = colorOptionDropdown.closest("custom-select");
          customSelect.selectOption(colorOptionDropdown);
        }
      }
    }

    /**
     * Updates the product media.
     * @param {string} mediaId - Id of the media item to show.
     */
    updateMedia(mediaId, isQuickAdd = false) {
      const img = this.productEl.querySelector(
        `[data-media-id="${mediaId}"] img`
      );

      if (!img) return;

      const src = img.src
        ? img.src.split("&width=")[0]
        : img.dataset.src.split("&width=")[0];
      const container = this.querySelector(".quick-add-info__media");
      const width = container.offsetWidth;
      const aspectRatio = img.width / img.height;
      container.innerHTML = `
        <img src="${src}&width=${width}" srcset="${src}&width=${width}, ${src}&width=${
        width * 2
      } 2x" width="${width * 2}" height="${(width * 2) / aspectRatio}" alt="${
        img.alt
      }">
      `;
    }

    /**
     * Builds the markup for the drawer content element.
     */
    updateContent() {
      if (this.template != "default") {
        this.content.innerHTML = `
        <svg id="stars" style="display: none;" version="1.1">
          <symbol id="stars-empty-star" width="19" height="19" viewBox="1 -1 14 21">
          <path fill-rule="evenodd" clip-rule="evenodd" d="M15.9144 6.556L15.9782 6.756C16.0307 6.90695 15.9865 7.07472 15.8665 7.18L11.9892 10.5L13.138 15.476C13.1769 15.635 13.1172 15.802 12.9864 15.9L12.8109 16.02C12.7427 16.0736 12.6582 16.1018 12.5716 16.1C12.4985 16.1015 12.4266 16.0821 12.3642 16.044L8.00016 13.38L3.66011 16.044C3.59771 16.0821 3.52573 16.1015 3.45267 16.1C3.36607 16.1018 3.28151 16.0736 3.21334 16.02L3.01388 15.9C2.88308 15.802 2.8234 15.635 2.8623 15.476L4.01114 10.5L0.14179 7.188C0.0156062 7.08334 -0.0321581 6.91092 0.0221197 6.756L0.109878 6.556C0.15654 6.39794 0.296829 6.28604 0.460912 6.276L5.55888 5.868L7.5135 1.156C7.57613 0.999515 7.72831 0.897785 7.89645 0.899999H8.10385C8.27049 0.896453 8.42105 0.999237 8.47881 1.156L10.4654 5.868L15.5634 6.276C15.7275 6.28604 15.8677 6.39794 15.9144 6.556ZM11.2871  " fill="transparent" stroke="#45C391" stroke-width="1.5"></path>  </symbol><symbol id="stars-full-star"  width="16" height="17"  viewBox="0 0 16 17" >
              <path d="M7.25672 1.4343C7.53172 0.781127 8.46832 0.781128 8.74332 1.4343L10.3663 5.28942C10.4823 5.56478 10.7445 5.75292 11.0454 5.77675L15.2582 6.11041C15.9719 6.16695 16.2614 7.04694 15.7175 7.50716L12.5079 10.2234C12.2786 10.4174 12.1784 10.7219 12.2485 11.0119L13.2291 15.0733C13.3952 15.7613 12.6375 16.3053 12.0264 15.9365L8.41965 13.7601C8.16202 13.6046 7.83802 13.6046 7.5804 13.7601L3.9736 15.9365C3.3625 16.3053 2.60477 15.7613 2.77091 15.0733L3.75155 11.0119C3.82159 10.7219 3.72147 10.4174 3.49221 10.2234L0.28245 7.50716C-0.261375 7.04694 0.0280544 6.16695 0.741835 6.11041L4.9547 5.77675C5.25561 5.75292 5.51774 5.56478 5.63367 5.28942L7.25672 1.4343Z" fill="#45C391" />
            </symbol>
            <symbol id="stars-half-star" width="16" height="17"  viewBox="0 0 16 17" >
              <path fill-rule="evenodd" clip-rule="evenodd" d="M15.9144 6.556L15.9782 6.756C16.0307 6.90695 15.9865 7.07472 15.8665 7.18L11.9892 10.5L13.138 15.476C13.1769 15.635 13.1172 15.802 12.9864 15.9L12.8109 16.02C12.7427 16.0736 12.6582 16.1018 12.5716 16.1C12.4985 16.1015 12.4266 16.0821 12.3642 16.044L8.00016 13.38L3.66011 16.044C3.59771 16.0821 3.52573 16.1015 3.45267 16.1C3.36607 16.1018 3.28151 16.0736 3.21334 16.02L3.01388 15.9C2.88308 15.802 2.8234 15.635 2.8623 15.476L4.01114 10.5L0.14179 7.188C0.0156062 7.08334 -0.0321581 6.91092 0.0221197 6.756L0.109878 6.556C0.15654 6.39794 0.296829 6.28604 0.460912 6.276L5.55888 5.868L7.5135 1.156C7.57613 0.999515 7.72831 0.897785 7.89645 0.899999H8.10385C8.27049 0.896453 8.42105 0.999237 8.47881 1.156L10.4654 5.868L15.5634 6.276C15.7275 6.28604 15.8677 6.39794 15.9144 6.556ZM11.2871 13.748L10.3936 10.004L13.3215 7.5L9.48409 7.196L8.00016 3.628V11.74L11.2871 13.748Z"  fill="#45C391" stroke="black"/>
            </symbol>
        </svg>
        <div class="quick-add-info grid mb-8 quick-add-info--${this.template}">
          <div class="quick-add-info__media${
            theme.settings.blendProductImages ? " image-blend" : ""
          }"></div>
          <div class="quick-add-info__details">
            <div class="product-price">
              ${
                this.productEl2.querySelector(".product-form__title-text")
                  .innerHTML
              }
            </div>
            <div class="text-theme-light text-sm mt-4">
              <a href="${this.productUrl}" class="link js-prod-link">
                ${theme.strings.viewDetails}
              </a>
            </div>
            <div class="product-prices">
              ${
                this.productEl2.querySelector(".product-form__prices").innerHTML
              }
            </div>
          </div>
          <div class="quick-add-info__details md:hidden"></div>
        </div>
        <div class="product-options">
          ${this.productEl2.querySelector(".product-options").innerHTML}
        </div>
        <div class="product-bienfaits">${
          this.productEl2.querySelector(".quick-add-check")
            ? this.productEl2.querySelector(".quick-add-check").innerHTML
            : ""
        }</div>
        <div class="product-ingredients">${
          this.productEl2.querySelector(".quick-add-ingredients")
            ? this.productEl2.querySelector(".quick-add-ingredients").innerHTML
            : ""
        }</div>
        <div class="product-reviews">${
          this.productEl2.querySelector(".quick-add__reviews")
            ? this.productEl2.querySelector(".quick-add__reviews").innerHTML
            : ""
        }</div>
        <div class="product-backorder">
          ${this.getElementHtml(".product-backorder")}
        </div>
      `;

        if (this.productEl2.querySelector(".rating-drawer")) {
          document
            .querySelector(".quick-add-info__details .product-title")
            .appendChild(this.productEl2.querySelector(".rating-drawer"));
        }

        if (
          document.querySelector(".product-ingredients .swiper-ingredientss")
        ) {
          new Swiper(".product-ingredients .swiper-ingredientss", {
            slidesPerView: 1,
            spaceBetween: 20,
            loop: true,
            navigation: {
              nextEl: ".product-ingredients .swiper-button-next",
              prevEl: ".product-ingredients .swiper-button-prev",
            },
            pagination: {
              el: ".product-ingredients .swiper-pagination",
              clickable: true,
            },
          });
        }

        if (document.querySelector(".product-reviews .swiper-review")) {
          new Swiper(".product-reviews .swiper-review", {
            slidesPerView: 1,
            spaceBetween: 20,
            loop: true,
            autoplay: {
              delay: Number(
                document.querySelector(".product-reviews .swiper-review")
                  .dataset.swiperDelay
              ),
            },
            navigation: {
              nextEl: ".product-reviews .swiper-button-next",
              prevEl: ".product-reviews .swiper-button-prev",
            },
            pagination: {
              el: ".product-reviews .swiper-pagination",
              clickable: true,
            },
          });
        }
      } else {
        this.content.innerHTML = `
        <div class="quick-add-info grid mb-8">
          <div class="quick-add-info__media${
            theme.settings.blendProductImages ? " image-blend" : ""
          }"></div>
          <div class="quick-add-info__details">
            <div class="product-vendor-sku mb-2 text-sm">
              ${this.getElementHtml(".product-vendor-sku")}
            </div>
            <div class="product-title">
              <a class="h6 js-prod-link" href="${this.productUrl}">
                ${this.getElementHtml(".product-title")}
              </a>
            </div>
            <hr>
            <div class="product-price">
              ${this.getElementHtml(".product-price")}
            </div>
            <div class="text-theme-light text-sm mt-4">
              <a href="${this.productUrl}" class="link js-prod-link">
                ${theme.strings.viewDetails}
              </a>
            </div>
          </div>
          <div class="quick-add-info__details md:hidden"></div>
        </div>
        <div class="product-options">
          ${this.getElementHtml(".product-options")}
        </div>
        <div class="product-backorder">
          ${this.getElementHtml(".product-backorder")}
        </div>
      `;
      }

      this.classList.remove("is-loading");
      this.content.classList.remove("drawer__content--out");
    }

    /**
     * Updates the Quick Add drawer form (buy buttons).
     */
    updateForm() {
      const productForm = this.productEl.querySelector("product-form");
      this.footer.classList.remove("quick-add__footer-message");

      if (productForm) {
        this.form.innerHTML = productForm.innerHTML;
        this.form.init();

        if (Shopify && Shopify.PaymentButton) {
          Shopify.PaymentButton.init();
        }
      } else {
        const signUpForm = this.productEl.querySelector(".product-signup");
        if (signUpForm) {
          this.form.innerHTML = signUpForm.innerHTML;
        } else {
          this.footer.classList.add("quick-add__footer-message");
          this.form.innerHTML = `
            <div class="alert quick-add__alert bg-info-bg text-info-text">
              <div class="flex">
                <div>
                  <svg class="icon icon--price_tag" width="32" height="32" viewBox="0 0 16 16" aria-hidden="true" focusable="false" role="presentation">
                    <path fill="currentColor" d="M7.59 1.34a1 1 0 01.7-.29h5.66a1 1 0 011 1v5.66a1 1 0 01-.3.7L7.6 15.5a1 1 0 01-1.42 0L.52 9.83a1 1 0 010-1.42l7.07-7.07zm6.36 6.37l-7.07 7.07-5.66-5.66L8.3 2.05h5.66v5.66z" fill-rule="evenodd"/>
                    <path fill="currentColor" d="M9.7 6.3a1 1 0 101.42-1.42 1 1 0 00-1.41 1.41zM9 7a2 2 0 102.83-2.83A2 2 0 009 7z" fill-rule="evenodd"/>
                  </svg>
                </div>
                <div>
                  <p class="text-h6 font-bold mb-2">${theme.strings.awaitingSale}</p>
                  <a class="link js-prod-link" href="${this.productUrl}">${theme.strings.viewDetails}</a>
                </div>
              </div>
            </div>`;
        }
      }

      this.footer.classList.remove("drawer__footer--out");
    }

    /**
     * Gets the innerHTML of an element within the product element.
     * @param {string} selector - CSS selector for the element.
     * @returns {?string}
     */
    getElementHtml(selector) {
      const el = this.productEl.querySelector(selector);
      return el ? el.innerHTML : "";
    }

    /**
     * Shows an "Added to cart" message in the drawer.
     */
    addedToCart() {
      if (this.notification) {
        setTimeout(() => {
          this.notification.hidden = false;
        }, 300);

        setTimeout(() => {
          this.notification.hidden = true;
          setTimeout(() => {
            this.close();
          }, 500);
        }, this.notification.dataset.visibleFor);
      }
    }
  }

  customElements.define("quick-add-drawer", QuickAddDrawer);
}
